import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Briefcase, ArrowRight, Building, User, Crown } from 'lucide-react';
import type { JobListing } from '@/types/jobWizard';
import { cn } from '@/lib/utils';
import MarketplaceListingCard from './marketplace/MarketplaceListingCard';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsTablet, useIsDesktop } from '@/hooks/use-tablet';
import { normalizeJobForCard } from '@/utils/marketplaceCardNormalizer';
import JobListingDetailModal from '@/components/jobs/JobListingDetailModal';

interface FeaturedJobsProps {
  className?: string;
}

const FeaturedJobs: React.FC<FeaturedJobsProps> = ({ className }) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const isMobileOrTablet = isMobile || isTablet;
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleJobClick = (job: JobListing) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };
  
  // Fetch featured job posts (companies hiring)
  const { data: featuredJobPosts, isLoading: loadingPosts } = useQuery({
    queryKey: ['featured-job-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_listings')
        .select('*')
        .eq('status', 'active')
        .in('listing_type', ['job_post', 'job_contract'])
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data as JobListing[];
    },
  });

  // Fetch featured professional profiles
  const { data: featuredProfiles, isLoading: loadingProfiles } = useQuery({
    queryKey: ['featured-job-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_listings')
        .select('*')
        .eq('status', 'active')
        .in('listing_type', ['job_profile', 'job_seeking'])
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data as JobListing[];
    },
  });

  const renderSkeleton = () => (
    <div className="space-y-3">
      <Skeleton className="h-36 w-full rounded-xl" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    </div>
  );

  const hasPosts = featuredJobPosts && featuredJobPosts.length > 0;
  const hasProfiles = featuredProfiles && featuredProfiles.length > 0;

  // If no featured content, show placeholder
  if (!loadingPosts && !loadingProfiles && !hasPosts && !hasProfiles) {
    return (
      <section className={cn("py-8 md:py-16 bg-background", className)}>
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-xl font-semibold text-muted-foreground mb-2">Coming Soon</h3>
            <p className="text-muted-foreground">Featured aviation jobs and professionals will appear here.</p>
            <Link to="/create-listing-jobs" className="mt-4 inline-block">
              <Button>Be the First to Post</Button>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn("py-8 md:py-16 bg-background", className)}>
      {/* Featured Job Posts Section */}
      {(loadingPosts || hasPosts) && (
        <>
          {/* Mobile only - inside container with full-width carousel */}
          {isMobile && (
            <div className="container mx-auto px-4 mb-12">
              {/* Section Header */}
              <div className="text-center mb-4 space-y-2">
                <div className="relative flex items-center justify-center w-full">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />
                  <div className="relative z-10 px-5 py-2 bg-background rounded-full border border-emerald-200 dark:border-emerald-800 shadow-sm backdrop-blur-sm flex items-center gap-2">
                    <Building className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <h3 className="text-base font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                      Featured Jobs
                    </h3>
                    {hasPosts && (
                      <Badge variant="secondary" className="text-xs ml-1">
                        {featuredJobPosts?.length}
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Swipe to browse</p>
              </div>

              {loadingPosts ? (
                <div className="grid grid-cols-1 gap-4">
                  {Array(2).fill(0).map((_, i) => <React.Fragment key={i}>{renderSkeleton()}</React.Fragment>)}
                </div>
              ) : (
                <Carousel opts={{ align: "start", loop: true }} className="w-full">
                  <CarouselContent className="-ml-2">
                    {featuredJobPosts?.map(job => (
                      <CarouselItem key={job.id} className="pl-2 basis-1/2 shrink-0 h-auto">
                        <MarketplaceListingCard
                          mobileCompact={true}
                          {...normalizeJobForCard(job)} 
                          onClick={() => handleJobClick(job)}
                          onViewDetails={() => handleJobClick(job)}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-emerald-500" />
                  <CarouselNext className="right-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-emerald-500" />
                </Carousel>
              )}

              {/* View All Link */}
              <div className="text-center mt-4">
                <Link to="/marketplace?asset=jobs&mode=sell">
                  <Button variant="outline" size="sm" className="gap-2">
                    View All Jobs
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Tablet + Desktop - outside container for full width like FeaturedHelicopters */}
          {!isMobile && (
            <div className="w-full px-8 lg:px-16 max-w-[2000px] mx-auto mb-12">
              <div className="relative">
                {/* Premium Background Layer */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/80 via-teal-50/60 to-emerald-50/80 dark:from-emerald-950/20 dark:via-teal-950/10 dark:to-emerald-950/20 rounded-3xl -z-10" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.1),transparent_50%)] rounded-3xl -z-10" />
                
                <div className="p-8 md:p-12">
                  {/* Centered Elegant Header */}
                  <div className="text-center mb-10 space-y-4">
                    <div className="relative flex items-center justify-center w-full">
                      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />
                      <div className="relative z-10 px-6 py-2.5 bg-background rounded-full border border-emerald-200 dark:border-emerald-800 shadow-sm backdrop-blur-sm flex items-center gap-2">
                        <Building className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <h3 className="text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                          Featured Jobs
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">Premium job listings with enhanced visibility</p>
                  </div>
                  
                  {loadingPosts ? (
                    <div className="grid grid-cols-4 gap-6">
                      {Array(4).fill(0).map((_, i) => <React.Fragment key={i}>{renderSkeleton()}</React.Fragment>)}
                    </div>
                  ) : (
                    <>
                      <Carousel
                        opts={{
                          align: "center",
                          loop: true,
                          containScroll: "trimSnaps",
                        }}
                        className="w-full"
                      >
                        <CarouselContent className="-ml-6">
                          {featuredJobPosts?.map(job => (
                            <CarouselItem key={job.id} className="pl-6 basis-[360px] shrink-0">
                              <MarketplaceListingCard 
                                {...normalizeJobForCard(job)} 
                                onClick={() => handleJobClick(job)}
                                onViewDetails={() => handleJobClick(job)}
                              />
                            </CarouselItem>
                          ))}
                        </CarouselContent>
                        
                        <CarouselPrevious className="left-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-emerald-500" />
                        <CarouselNext className="right-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-emerald-500" />
                      </Carousel>
                      
                      <div className="text-center mt-8">
                        <Link to="/marketplace?asset=jobs&mode=sell">
                          <Button variant="outline" className="gap-2">
                            View All Jobs
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Featured Professionals Section */}
      {(loadingProfiles || hasProfiles) && (
        <>
          {/* Mobile only - inside container with full-width carousel */}
          {isMobile && (
            <div className="container mx-auto px-4">
              {/* Section Header */}
              <div className="text-center mb-4 space-y-2">
                <div className="relative flex items-center justify-center w-full">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
                  <div className="relative z-10 px-5 py-2 bg-background rounded-full border border-amber-200 dark:border-amber-800 shadow-sm backdrop-blur-sm flex items-center gap-2">
                    <User className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <h3 className="text-base font-semibold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                      Featured Professionals
                    </h3>
                    {hasProfiles && (
                      <Badge variant="secondary" className="text-xs ml-1">
                        {featuredProfiles?.length}
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Swipe to browse</p>
              </div>

              {loadingProfiles ? (
                <div className="grid grid-cols-1 gap-4">
                  {Array(2).fill(0).map((_, i) => <React.Fragment key={i}>{renderSkeleton()}</React.Fragment>)}
                </div>
              ) : (
                <Carousel opts={{ align: "start", loop: true }} className="w-full">
                  <CarouselContent className="-ml-2">
                    {featuredProfiles?.map(profile => (
                      <CarouselItem key={profile.id} className="pl-2 basis-1/2 shrink-0 h-auto">
                        <MarketplaceListingCard
                          mobileCompact={true}
                          {...normalizeJobForCard(profile)} 
                          onClick={() => handleJobClick(profile)}
                          onViewDetails={() => handleJobClick(profile)}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                  <CarouselNext className="right-2 h-9 w-9 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                </Carousel>
              )}

              {/* View All Link */}
              <div className="text-center mt-4">
                <Link to="/marketplace?asset=jobs&mode=buy">
                  <Button variant="outline" size="sm" className="gap-2">
                    View All Professionals
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Tablet + Desktop - outside container for full width like FeaturedHelicopters */}
          {!isMobile && (
            <div className="w-full px-8 lg:px-16 max-w-[2000px] mx-auto">
              <div className="relative">
                {/* Premium Background Layer */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-amber-50/80 dark:from-amber-950/20 dark:via-orange-950/10 dark:to-amber-950/20 rounded-3xl -z-10" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(245,158,11,0.1),transparent_50%)] rounded-3xl -z-10" />
                
                <div className="p-8 md:p-12">
                  {/* Centered Elegant Header */}
                  <div className="text-center mb-10 space-y-4">
                    <div className="relative flex items-center justify-center w-full">
                      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-amber-500 to-transparent" />
                      <div className="relative z-10 px-6 py-2.5 bg-background rounded-full border border-amber-200 dark:border-amber-800 shadow-sm backdrop-blur-sm flex items-center gap-2">
                        <User className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <h3 className="text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                          Featured Professionals
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">Premium profiles with enhanced visibility</p>
                  </div>
                  
                  {loadingProfiles ? (
                    <div className="grid grid-cols-4 gap-6">
                      {Array(4).fill(0).map((_, i) => <React.Fragment key={i}>{renderSkeleton()}</React.Fragment>)}
                    </div>
                  ) : (
                    <>
                      <Carousel
                        opts={{
                          align: "center",
                          loop: true,
                          containScroll: "trimSnaps",
                        }}
                        className="w-full"
                      >
                        <CarouselContent className="-ml-6">
                          {featuredProfiles?.map(profile => (
                            <CarouselItem key={profile.id} className="pl-6 basis-[360px] shrink-0">
                              <MarketplaceListingCard 
                                {...normalizeJobForCard(profile)} 
                                onClick={() => handleJobClick(profile)}
                                onViewDetails={() => handleJobClick(profile)}
                              />
                            </CarouselItem>
                          ))}
                        </CarouselContent>
                        
                        <CarouselPrevious className="left-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                        <CarouselNext className="right-4 h-12 w-12 bg-white/90 hover:bg-white dark:bg-background/90 dark:hover:bg-background shadow-lg border-2 border-amber-500" />
                      </Carousel>
                      
                      <div className="text-center mt-8">
                        <Link to="/marketplace?asset=jobs&mode=buy">
                          <Button variant="outline" className="gap-2">
                            View All Professionals
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobListingDetailModal
          listing={selectedJob}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </section>
  );
};

export default FeaturedJobs;